import { faCalendarPlus, faUserPlus, faUsers, faSpinner } from '@fortawesome/pro-regular-svg-icons';
import { faFileExcel } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button, Grid, TableCell, Typography
} from '@mui/material';
import {
  GenericListContainer, GenericListFilters, GenericTable, SkeletonLine,
  TextError
} from 'components';
import { useModal } from 'hooks';
import { useEffectAfterRender } from 'hooks/UseEffectAfterRender';
import useInfiniteLoading from 'hooks/useInfiniteList';
import { useSnackbar } from 'notistack';
import { PageTitle } from 'pages';
import React, { useCallback, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { UserService } from 'services';
import shortid from 'shortid';
import { DocumentHelper, formatDate, translate } from 'utils';
import { API_ROUTES } from 'utils/constants';
import { UserListFilters } from './UserListFilters';

type HeaderType<T = any> = {
  isSortable?: boolean;
  label: string;
  name: string;
  sortOn?: string;
  template: (row: T) => React.ReactElement;
};

const listHeaders = (): HeaderType<any>[] => [
  {
    name: 'fullName',
    label: translate('common.lastName'),
    isSortable: true,
    template: row => (
        <TableCell key={shortid.generate()} style={{ maxWidth: '200px' }}>
          <Typography style={{ wordBreak: 'break-word' }}>
            {(row.fullName && row.fullName.trim()) || '-'}
          </Typography>
        </TableCell>
    )
  }, {
    name: 'email',
    label: translate('common.email'),
    template: row => (
        <TableCell key={shortid.generate()} style={{ maxWidth: '200px' }}>
          <Typography style={{ wordBreak: 'break-word' }}>
            {row.email || '-'}
          </Typography>
        </TableCell>
    )
  }, {
    name: 'login',
    label: translate('common.login'),
    template: row => (
        <TableCell key={shortid.generate()} style={{ maxWidth: '200px', minWidth: '150px' }}>
          <Typography style={{ wordBreak: 'break-word' }}>
            {row.login || '-'}
          </Typography>
        </TableCell>
    )
  }, {
    name: 'creationDate',
    label: translate('common.creationDate'),
    isSortable: true,
    template: row => (
        <TableCell key={shortid.generate()}>
          {row.creationDate && (
              <span>
            <FontAwesomeIcon icon={faCalendarPlus} style={{ marginRight: '1rem' }} />
                {formatDate(row.creationDate)}
          </span>
          )}
        </TableCell>
    )
  }, {
    name: 'company',
    label: translate('common.company'),
    sortOn: 'label',
    template: row => (
        <TableCell key={shortid.generate()}>
          {row.companyName || ''}
        </TableCell>
    )
  }, {
    name: 'accountActivated',
    label: translate('common.userAccount'),
    template: row => (
        <TableCell key={shortid.generate()}>
        <span>
          {row.hasAccount ? translate('common.yes') : translate('common.no')}
          {(row.hasAccount && !row.accountActivated) && ` (${translate('common.deactivated')})`}
        </span>
        </TableCell>
    )
  },
  {
    name: 'userAccountApplications',
    label: translate('common.userAccountApplications'),
    template: row => (
        <TableCell key={shortid.generate()} style={{ minWidth: '150px' }}>
          <Typography style={{ wordBreak: 'break-word' }}>
            {row.userAccountApplications || ''}
          </Typography>
        </TableCell>
    )
  }
];

const defaultAppFilter = (appName: string | null) => {
  if (!appName) {
    return null;
  }

  return [
    {
      key: 'applications',
      label: appName,
      value: [{ value: appName, label: appName }]
    }
  ];
};

export const UserList = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const displayModal = useModal();
  const originApp = location.search && location.search.split('=')[1].toUpperCase();

  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState(defaultAppFilter(originApp) ?? []);
  const [isExporting, setIsExporting] = useState(false);

  const getUserList = useCallback((page: number) => {
    let finalFilters = {
      companyName: '',
      application: [] as any[],
      roles: [] as any[],
      siren: '',
      siret: '',
      institutionName: '',
      postalCode: '',
      city: '',
      active: null
    };

    filters.forEach(filter => {
      switch (filter.key) {
        case 'applications':
        case 'roles':
          finalFilters[filter.key] = filter.value;
          break;
        case 'active':
          finalFilters.active = filter.value;
          break;
        default:
          // Tous les autres champs sont textuels
          finalFilters[filter.key] = filter.label;
      }
    });

    console.log('[UserList] finalFilters:', finalFilters);

        return UserService.getUserList({ freeSearch: search, ...finalFilters }, page)
            .then(res => {
                console.log('[UserList] API response:', res);
                return res;
            });
  }, [filters, search]);

  const {
    items, hasMore, isLoading, totalSize, reloadList, loadMore
  } = useInfiniteLoading(getUserList);

  useEffectAfterRender(() => {
    reloadList();
  }, [filters, search]);

  const handleRowClick = useCallback((row: { personId: string }) => navigate(API_ROUTES.USER_DETAIL(row.personId)), [navigate]);

  const createUser = useCallback((form: any) => {
    UserService.createUser(form)
        .then(response => {
          enqueueSnackbar(translate('confirms.userList.createAdmin'), { variant: 'success' });
          handleRowClick({ personId: response });
        })
        .catch(error => displayModal({ type: 'ERROR', text: error }));
  }, [enqueueSnackbar, handleRowClick, displayModal]);

  const displayModalCreateUser = useCallback(() => displayModal({
    type: 'CREATE_USER',
    onConfirm: createUser,
    closeOnSubmit: true
  }), [displayModal, createUser]);

  const exportUsersToExcel = useCallback(() => {
      setIsExporting(true);

      // Construire les filtres comme pour la recherche
      const finalFilters = {
        companyName: '',
        application: [],
        roles: [],
        siren: '',
        siret: '',
        institutionName: '',
        postalCode: '',
        city: '',
        active: null
      };

      filters.forEach(filter => {
        switch (filter.key) {
          case 'applications':
          case 'roles':
            finalFilters[filter.key] = filter.value;
            break;
          case 'active':
            finalFilters.active = filter.value;
            break;
          default:
            finalFilters[filter.key] = filter.label;
        }
      });

      const params = { freeSearch: search, ...finalFilters };

      UserService.exportUsersAccounts(params)
        .then(response => {
          const downloadLink = document.createElement('a');
          downloadLink.href = DocumentHelper.getExcelWithBase64(response.base64Content);
          downloadLink.download = response.name;
          downloadLink.click();
        })
        .catch((error) => enqueueSnackbar(error?.message || error, { variant: 'error', autoHideDuration: 5000 }))
        .finally(() => setIsExporting(false));
  }, [filters, search, enqueueSnackbar]);

  const renderButtonContainer = () => (
    <div className="buttonsContainer">
      <Button
        color="primary"
        data-cy="createNewUser"
        startIcon={<FontAwesomeIcon icon={faUserPlus} />}
        variant="contained"
        onClick={displayModalCreateUser}
      >
        {translate('button.create')}
      </Button>

      <Button
        color="secondary"
        data-cy="exportUsers"
        disabled={isExporting || isLoading}
        startIcon={<FontAwesomeIcon icon={isExporting ? faSpinner : faFileExcel} spin={isExporting} />}
        variant="contained"
        onClick={exportUsersToExcel}
      >
        {translate('button.export')}
      </Button>
    </div>
  );

  const renderFilters = useCallback(({ currentFilters, setCurrentFilters }: {
    currentFilters: any[],
    setCurrentFilters: (filters: any[]) => void
  }) => (
      <UserListFilters
          currentFilters={currentFilters}
          setCurrentFilters={setCurrentFilters}
      />
  ), []);

  return (
      <GenericListContainer>
        <PageTitle title={translate('pageUserList.title')} titleRight={renderButtonContainer()} />
        <GenericListFilters
            ComponentFilter={renderFilters}
            dataTour="step-admin-userFilters"
            filters={filters}
            search={search}
            setFilters={setFilters}
            setSearch={setSearch}
            tooltipInfo="pageUserList.searchTooltip"
            withDrawer
        />

        <div data-tour="step-admin-userList">
          {!isLoading && items.length === 0
              ? (
                  <Grid alignItems="center" container direction="column">
                    <FontAwesomeIcon color="var(--primary-color)" icon={faUsers} size="3x" />
                    <TextError>{translate('errors.noUser')}</TextError>
                    <Button
                        color="primary"
                        data-cy="createNewUser"
                        startIcon={<FontAwesomeIcon icon={faUserPlus} />}
                        variant="contained"
                        onClick={displayModalCreateUser}
                    >
                      {translate('button.createUser')}
                    </Button>
                  </Grid>
              ) : (
                  <GenericTable
                      dataCy="userList"
                      hasMore={hasMore}
                      headers={listHeaders()}
                      isLoading={isLoading}
                      loadMore={loadMore}
                      rows={items}
                      total={totalSize}
                      onRowClick={handleRowClick}
                  />
              )}
          {isLoading && items.length === 0 && <SkeletonLine />}

        </div>
      </GenericListContainer>
  );
};